/*
 * Wafflepupz shared behaviours for the Horizon theme.
 * Plain ES2020 script, defer-loaded, no modules, no dependencies.
 * Safe to run twice: state lives on window.WP, global listeners are bound once
 * and always delegate to the latest handlers; init() re-runs on shopify:section:load.
 *
 * Exposes window.WP:
 *   WP.cartCount()               fetch /cart.js and write item_count to [data-wp-cart-count]
 *   WP.openCart(event)           open Monster Cart (fallback: location.hash = 'cart')
 *   WP.refreshCart(open)         monster_refresh(open, cb) or cartCount()
 *   WP.addToCart(formData)       POST /cart/add.js then refreshCart(true)
 *   WP.scrollToWithHeader(el, extra)  scroll so el sits under the fixed header
 *   WP.scrollTop() / WP.scrollToY(y) / WP.headerHeight() / WP.isTouch() / WP.init()
 */
(function () {
  'use strict';

  var WP = window.WP || {};
  window.WP = WP;

  var COMPACT_ON = 60;
  var COMPACT_OFF = 20;

  // Persistent state (survives a second execution of this file).
  var state = (WP._state = WP._state || {
    header: null,
    spacer: null,
    spacerHeight: 0,
    resizeObserver: null,
    scrollTicking: false,
    bound: false,
  });

  // Handlers are re-assigned on every execution; listeners delegate through WP._h.
  var h = (WP._h = {});

  /* ---------- helpers ---------- */

  WP.isTouch = function () {
    return !!(window.matchMedia && window.matchMedia('(hover: none)').matches);
  };

  function pageWrapper() {
    return document.querySelector('.page-wrapper');
  }

  /* Horizon can make .page-wrapper the scroll container on desktop; read both. */
  WP.scrollTop = function () {
    var y = window.scrollY || window.pageYOffset || 0;
    var pw = pageWrapper();
    if (pw && pw.scrollTop > y) y = pw.scrollTop;
    return y;
  };

  function innerScroller() {
    var pw = pageWrapper();
    if (!pw) return null;
    var style = window.getComputedStyle(pw);
    if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && pw.scrollHeight > pw.clientHeight + 1) {
      return pw;
    }
    return null;
  }

  WP.scrollToY = function (y) {
    var target = innerScroller() || window;
    y = Math.max(0, Math.round(y));
    try {
      target.scrollTo({ top: y, behavior: 'smooth' });
    } catch (e) {
      if (target === window) window.scrollTo(0, y);
      else target.scrollTop = y;
    }
  };

  WP.headerHeight = function () {
    var v = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--wp-header-h'));
    return isNaN(v) ? 0 : v;
  };

  WP.scrollToWithHeader = function (el, extra) {
    if (!el || !el.getBoundingClientRect) return;
    if (typeof extra !== 'number') extra = 12;
    var y = el.getBoundingClientRect().top + WP.scrollTop() - WP.headerHeight() - extra;
    WP.scrollToY(y);
  };

  /* ---------- cart ---------- */

  WP.setCartCount = function (n) {
    n = parseInt(n, 10) || 0;
    var els = document.querySelectorAll('[data-wp-cart-count]');
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = String(n);
      els[i].setAttribute('data-count', String(n));
      els[i].hidden = n === 0;
    }
    return n;
  };

  WP.cartCount = function () {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        if (cart && typeof cart.item_count !== 'undefined') WP.setCartCount(cart.item_count);
        return cart;
      })
      .catch(function () {
        return null;
      });
  };

  WP.openCart = function (event) {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    if (typeof window.openCart === 'function') {
      window.openCart();
    } else {
      window.location.hash = 'cart';
    }
  };

  WP.refreshCart = function (open) {
    if (typeof window.monster_refresh === 'function') {
      window.monster_refresh(!!open, WP.cartCount);
    } else {
      WP.cartCount();
    }
  };

  WP.addToCart = function (formData) {
    var init = { method: 'POST', headers: { Accept: 'application/json' } };
    if (typeof FormData !== 'undefined' && formData instanceof FormData) {
      init.body = formData;
    } else {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(formData || {});
    }
    return fetch('/cart/add.js', init)
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      })
      .then(function (data) {
        WP.refreshCart(true);
        try {
          document.dispatchEvent(new CustomEvent('wp:cart:added', { detail: data }));
        } catch (e) {
          /* ignore */
        }
        return data;
      });
  };

  /* ---------- cart: catch Monster Cart's own add/change/update/clear calls ---------- */

  var CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var cartWatchTimer = null;

  function scheduleCartCountRefresh() {
    if (cartWatchTimer) clearTimeout(cartWatchTimer);
    cartWatchTimer = setTimeout(function () {
      cartWatchTimer = null;
      WP.cartCount();
    }, 150);
  }

  function watchCartRequests() {
    if (window.fetch && !window.fetch.__wpCartWatched) {
      var origFetch = window.fetch;
      var wrappedFetch = function () {
        var url = arguments[0];
        var watch = false;
        try {
          if (url && typeof url === 'object') url = url.url || String(url);
          watch = typeof url === 'string' && CART_URL_RE.test(url);
        } catch (e) {
          /* ignore */
        }
        var result = origFetch.apply(this, arguments);
        /* Refresh once the request has finished (not when it starts), on a side branch so the caller's promise is untouched. */
        if (watch && result && typeof result.then === 'function') {
          try {
            result.then(scheduleCartCountRefresh, scheduleCartCountRefresh);
          } catch (e) {
            /* ignore */
          }
        }
        return result;
      };
      wrappedFetch.__wpCartWatched = true;
      window.fetch = wrappedFetch;
    }

    if (window.XMLHttpRequest && !window.XMLHttpRequest.prototype.__wpCartWatched) {
      var proto = window.XMLHttpRequest.prototype;
      var origOpen = proto.open;
      var origSend = proto.send;
      proto.open = function (method, url) {
        try {
          this.__wpCartUrl = url;
        } catch (e) {
          /* ignore */
        }
        return origOpen.apply(this, arguments);
      };
      proto.send = function () {
        try {
          var url = this.__wpCartUrl;
          if (typeof url === 'string' && CART_URL_RE.test(url)) {
            this.addEventListener('loadend', scheduleCartCountRefresh);
          }
        } catch (e) {
          /* ignore */
        }
        return origSend.apply(this, arguments);
      };
      proto.__wpCartWatched = true;
    }
  }

  /* ---------- header: compact mode + height variable ---------- */

  h.writeHeaderHeight = function () {
    var header = state.header;
    if (!header || !header.isConnected) return;
    var height = header.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--wp-header-h', Math.round(height) + 'px');
    // The spacer keeps the full (uncompact) height so content never jumps.
    if (!header.classList.contains('is-compact') || !state.spacerHeight) state.spacerHeight = height;
    if (state.spacer) state.spacer.style.height = Math.round(state.spacerHeight) + 'px';
  };

  h.updateCompact = function () {
    var header = state.header;
    if (!header) return;
    var y = WP.scrollTop();
    var compact = header.classList.contains('is-compact');
    if (!compact && y > COMPACT_ON) {
      header.classList.add('is-compact');
      h.writeHeaderHeight();
    } else if (compact && y < COMPACT_OFF) {
      header.classList.remove('is-compact');
      h.writeHeaderHeight();
    }
  };

  h.onScroll = function () {
    if (state.scrollTicking) return;
    state.scrollTicking = true;
    window.requestAnimationFrame(function () {
      state.scrollTicking = false;
      WP._h.updateCompact();
    });
  };

  h.initHeader = function () {
    var header = document.querySelector('[data-wp-header]');
    if (!header) return;
    var isNew = header !== state.header;
    state.header = header;
    state.spacer = document.querySelector('[data-wp-header-spacer]');
    if (isNew) state.spacerHeight = 0;

    if (state.resizeObserver) state.resizeObserver.disconnect();
    if (window.ResizeObserver) {
      state.resizeObserver = new ResizeObserver(function () {
        WP._h.writeHeaderHeight();
      });
      state.resizeObserver.observe(header);
    }

    // Measure the full height before the compact class can be applied.
    header.classList.remove('is-compact');
    h.writeHeaderHeight();
    h.updateCompact();
    // The logo shrink transition changes the height: re-measure when it ends.
    if (!header.hasAttribute('data-wp-bound')) {
      header.setAttribute('data-wp-bound', '');
      header.addEventListener('transitionend', function () {
        WP._h.writeHeaderHeight();
      });
    }
  };

  /* ---------- accordions: one open at a time ---------- */

  // Lets section-level fallbacks (e.g. wp-details-accordion) skip their own one-open logic.
  WP.accordionHandled = true;

  h.onAccordionToggle = function (event) {
    var details = event.currentTarget;
    if (!details.open) return;
    var list = details.closest('[data-wp-accordion]');
    if (!list) return;
    var siblings = list.querySelectorAll('details');
    for (var i = 0; i < siblings.length; i++) {
      if (siblings[i] !== details && siblings[i].open) siblings[i].open = false;
    }
  };

  h.initAccordions = function () {
    var lists = document.querySelectorAll('[data-wp-accordion]');
    for (var i = 0; i < lists.length; i++) {
      var items = lists[i].querySelectorAll('details');
      for (var j = 0; j < items.length; j++) {
        if (items[j].hasAttribute('data-wp-bound')) continue;
        items[j].setAttribute('data-wp-bound', '');
        items[j].addEventListener('toggle', function (event) {
          WP._h.onAccordionToggle(event);
        });
      }
    }
  };

  /* ---------- hash-driven tabs ---------- */

  h.tabsActivate = function (root, key, updateHash) {
    var tabs = root.querySelectorAll('[data-wp-tab]');
    var panels = root.querySelectorAll('[data-wp-panel]');
    var found = false;
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i].getAttribute('data-wp-tab') === key) found = true;
    }
    if (!found) return false;
    for (var t = 0; t < tabs.length; t++) {
      var on = tabs[t].getAttribute('data-wp-tab') === key;
      tabs[t].classList.toggle('is-active', on);
      tabs[t].setAttribute('aria-selected', on ? 'true' : 'false');
      if (tabs[t].tagName === 'BUTTON' || tabs[t].tagName === 'A') tabs[t].setAttribute('tabindex', on ? '0' : '-1');
    }
    for (var p = 0; p < panels.length; p++) {
      var show = panels[p].getAttribute('data-wp-panel') === key;
      panels[p].hidden = !show;
      panels[p].classList.toggle('is-active', show);
    }
    root.setAttribute('data-wp-tabs-active', key);
    if (updateHash) {
      try {
        window.history.replaceState(null, '', '#' + key);
      } catch (e) {
        /* ignore */
      }
    }
    try {
      root.dispatchEvent(new CustomEvent('wp:tabs:change', { detail: { key: key }, bubbles: true }));
    } catch (e) {
      /* ignore */
    }
    return true;
  };

  h.tabsInitialKey = function (root) {
    var hash = (window.location.hash || '').replace(/^#/, '');
    var tabs = root.querySelectorAll('[data-wp-tab]');
    var i;
    if (hash) {
      for (i = 0; i < tabs.length; i++) {
        if (tabs[i].getAttribute('data-wp-tab') === hash) return hash;
      }
    }
    for (i = 0; i < tabs.length; i++) {
      if (tabs[i].classList.contains('is-active')) return tabs[i].getAttribute('data-wp-tab');
    }
    return tabs.length ? tabs[0].getAttribute('data-wp-tab') : null;
  };

  h.onTabClick = function (event) {
    var tab = event.target.closest('[data-wp-tab]');
    if (!tab) return;
    var root = event.currentTarget;
    if (!root.contains(tab)) return;
    event.preventDefault();
    h.tabsActivate(root, tab.getAttribute('data-wp-tab'), true);
  };

  h.initTabs = function () {
    var roots = document.querySelectorAll('[data-wp-tabs]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (!root.hasAttribute('data-wp-bound')) {
        root.setAttribute('data-wp-bound', '');
        root.addEventListener('click', function (event) {
          WP._h.onTabClick(event);
        });
      }
      var key = h.tabsInitialKey(root);
      if (key) h.tabsActivate(root, key, false);
    }
  };

  h.onHashChange = function () {
    var hash = (window.location.hash || '').replace(/^#/, '');
    if (!hash) return;
    var roots = document.querySelectorAll('[data-wp-tabs]');
    for (var i = 0; i < roots.length; i++) {
      h.tabsActivate(roots[i], hash, false);
    }
  };

  /* ---------- delegated clicks: cart icon, product cards on touch ---------- */

  h.onDocumentClick = function (event) {
    var cartLink = event.target.closest('[data-wp-open-cart]');
    if (cartLink) {
      WP.openCart(event);
      return;
    }

    /* Colour swatches. Mouse: a click opens the colour's variant (href). Touch: the first tap
       shows that colour on the card and marks the dot; a second tap on the same dot, or a tap on
       the photo / title while a colour is shown, opens the product in that colour. */
    var swatch = event.target.closest('.wp-card__swatch-link');
    if (swatch) {
      if (!WP.isTouch() || swatch.classList.contains('is-active')) return;
      event.preventDefault();
      var swatchCard = swatch.closest('[data-wp-card]');
      h.cardPreload(swatchCard);
      h.cardSwatchOn(swatch);
      return;
    }
    if (WP.isTouch()) {
      var cardLink = event.target.closest('a');
      var colourCard = cardLink && cardLink.closest('[data-wp-card]');
      var picked = colourCard && colourCard.querySelector('.wp-card__swatch-link.is-active');
      if (picked && !cardLink.classList.contains('wp-card__swatch-link')) {
        event.preventDefault();
        window.location.href = picked.href;
        return;
      }
    }

    var card = event.target.closest('.wp-card');
    if (!card || !WP.isTouch()) return;
    if (!card.querySelector('.wp-card__img--hover')) return;
    if (card.classList.contains('is-hover')) return; // second tap follows the link
    event.preventDefault();
    h.loadCardHover(card);
    var others = document.querySelectorAll('.wp-card.is-hover');
    for (var i = 0; i < others.length; i++) others[i].classList.remove('is-hover');
    card.classList.add('is-hover');
  };

  /* ---------- deferred images (wp-image `defer: true`, e.g. card hover photos) ---------- */

  /* Swaps data-wp-srcset / data-wp-src into srcset / src for every deferred image inside `root`. */
  WP.loadDeferred = function (root) {
    if (!root || !root.querySelectorAll) return;
    var els = Array.prototype.slice.call(root.querySelectorAll('[data-wp-srcset], [data-wp-src]'));
    if (root.hasAttribute && (root.hasAttribute('data-wp-srcset') || root.hasAttribute('data-wp-src'))) els.unshift(root);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var set = el.getAttribute('data-wp-srcset');
      if (set) {
        el.setAttribute('srcset', set);
        el.removeAttribute('data-wp-srcset');
      }
      var src = el.getAttribute('data-wp-src');
      if (src) {
        el.setAttribute('src', src);
        el.removeAttribute('data-wp-src');
      }
    }
  };

  /* Below-the-fold photos (wp-image `defer: 'view'` / snippet wp-defer-view): the <img> carries
     data-wp-defer="view" and loads once it is within 1000 px (phones) / 1200 px (desktop) of
     the screen. Photos in a horizontal scroll row load together with the first one that comes
     near, so swiping the row never shows empty frames. */
  h.hScroller = function (el) {
    var node = el.parentElement;
    for (var depth = 0; node && node !== document.body && depth < 10; depth++) {
      if (node.scrollWidth > node.clientWidth + 1) {
        var ox = window.getComputedStyle(node).overflowX;
        if (ox === 'auto' || ox === 'scroll') return node;
      }
      node = node.parentElement;
    }
    return null;
  };

  h.revealDeferred = function (el) {
    var row = h.hScroller(el);
    var list = row ? row.querySelectorAll('img[data-wp-defer="view"]') : [el];
    for (var i = 0; i < list.length; i++) {
      var img = list[i];
      if (state.deferIO) state.deferIO.unobserve(img);
      if (state.deferNowIO) state.deferNowIO.unobserve(img);
      img.removeAttribute('data-wp-defer');
      WP.loadDeferred(img.parentNode && img.parentNode.tagName === 'PICTURE' ? img.parentNode : img);
    }
    if (state.prewarmOn) WP._h.prewarmPump();
  };

  h.revealAllDeferred = function () {
    var els = document.querySelectorAll('img[data-wp-defer="view"]');
    for (var i = 0; i < els.length; i++) WP._h.revealDeferred(els[i]);
  };

  /* Two observers: until the page has loaded (or the visitor scrolls, touches or types) only
     photos that are actually on screen load, so nothing below the fold competes with the hero
     photo (LCP). After that the look-ahead observer loads photos 1000 px / 1200 px early, and
     after load the next 2 photos below that are fetched at low priority, so a slow phone that
     scrolls never meets an empty frame. */
  h.deferObserver = function (margin) {
    return new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) WP._h.revealDeferred(entries[i].target);
        }
      },
      /* Horizon can make .page-wrapper the scroll container; the margin must apply to it. */
      { root: innerScroller(), rootMargin: margin + ' 0px' }
    );
  };

  h.armDeferredView = function () {
    if (state.deferArmed) return;
    state.deferArmed = true;
    var wide = !!(window.matchMedia && window.matchMedia('(min-width: 768px)').matches);
    if (state.deferNowIO) state.deferNowIO.disconnect();
    state.deferNowIO = null;
    state.deferIO = h.deferObserver(wide ? '1200px' : '1000px');
    WP._h.initDeferredView();
    WP._h.schedulePrewarm();
  };

  /* Pre-warm: once the page has loaded and LCP has been recorded (else 1 s after the load
     event), the next 2 still-waiting photos below the look-ahead are fetched in page order at
     low priority, one at a time. When the visitor scrolls on and the look-ahead takes them over,
     the next ones follow, so there are always 2 ready ahead. Only the single <img> gets its
     sources; it keeps data-wp-defer, so the look-ahead observer still reveals it (and its row). */
  h.schedulePrewarm = function () {
    if (state.prewarmScheduled) return;
    state.prewarmScheduled = true;
    var lcpSeen = false;
    try {
      new PerformanceObserver(function () { lcpSeen = true; }).observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {}
    var start = function () {
      state.prewarmOn = true;
      WP._h.prewarmPump();
    };
    var go = function () {
      setTimeout(function () {
        if (lcpSeen) start();
        else setTimeout(start, 1000);
      }, 0);
    };
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  };

  h.PREWARM_AHEAD = 2;

  h.prewarmPump = function () {
    if (!state.prewarmOn || state.prewarmBusy) return;
    var els = document.querySelectorAll('img[data-wp-defer="view"]');
    var ahead = 0;
    var next = null;
    for (var i = 0; i < els.length; i++) {
      /* Skip images that are not laid out (display: none, e.g. a hidden variant gallery). */
      if (!els[i].getClientRects().length) continue;
      if (els[i].wpPrewarmed) {
        ahead++;
        continue;
      }
      next = els[i];
      break;
    }
    if (!next || ahead >= h.PREWARM_AHEAD) return;
    var img = next;
    img.wpPrewarmed = true;
    state.prewarmBusy = true;
    var timer = null;
    var done = function () {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      if (timer) clearTimeout(timer);
      state.prewarmBusy = false;
      WP._h.prewarmPump();
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
    timer = setTimeout(done, 15000);
    img.setAttribute('fetchpriority', 'low');
    img.setAttribute('loading', 'eager');
    WP.loadDeferred(img.parentNode && img.parentNode.tagName === 'PICTURE' ? img.parentNode : img);
  };

  h.initDeferredView = function () {
    var els = document.querySelectorAll('img[data-wp-defer="view"]');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      h.revealAllDeferred();
      return;
    }
    if (!state.deferArmed && !state.deferNowIO) {
      state.deferNowIO = h.deferObserver('0px');
      var arm = function () {
        ARM_EVENTS.forEach(function (ev) { window.removeEventListener(ev, arm, true); });
        WP._h.armDeferredView();
      };
      var ARM_EVENTS = ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'];
      ARM_EVENTS.forEach(function (ev) { window.addEventListener(ev, arm, { capture: true, passive: true }); });
      if (document.readyState === 'complete') arm();
      else window.addEventListener('load', arm);
    }
    var io = state.deferArmed ? state.deferIO : state.deferNowIO;
    var key = state.deferArmed ? 'wpDeferArmed' : 'wpDeferNow';
    for (var i = 0; i < els.length; i++) {
      if (els[i][key]) continue;
      els[i][key] = true;
      io.observe(els[i]);
    }
  };

  /* Card hover photos: never on phones/touch at page load. Hover devices get them after the page
     has loaded (still loading="lazy", so only near the viewport); touch devices at the first tap. */
  h.loadCardHover = function (card) {
    if (!card) return;
    var layers = card.querySelectorAll('.wp-card__img--hover');
    for (var i = 0; i < layers.length; i++) {
      WP.loadDeferred(layers[i]);
      h.markLoaded(layers[i]);
    }
  };

  /* Adds .is-loaded to a hover layer once its photo has loaded. Until then the CSS keeps the layer
     hidden, so a first tap on a phone keeps showing the main photo instead of the beige
     placeholder while the hover photo downloads. */
  h.markLoaded = function (layer) {
    var img = layer.querySelector('img');
    if (!img) return;
    /* Deferred images carry a 1x1 data: GIF until the real photo is chosen, so only count a
       load of a real (non data:) source. */
    var real = function () {
      return img.complete && img.naturalWidth > 1 && (img.currentSrc || '').indexOf('data:') !== 0;
    };
    var onLoad = function () {
      if (!real()) return;
      layer.classList.add('is-loaded');
      img.removeEventListener('load', onLoad);
    };
    if (real()) layer.classList.add('is-loaded');
    else img.addEventListener('load', onLoad);
  };

  h.loadAllCardHovers = function () {
    if (WP.isTouch()) return;
    var cards = document.querySelectorAll('[data-wp-card]');
    for (var i = 0; i < cards.length; i++) h.loadCardHover(cards[i]);
  };

  /* ---------- product card colour swatches: hover preview ---------- */

  /* Preload the card's swatch images (small width) the first time the pointer enters the card */
  h.cardPreload = function (card) {
    if (!card || card.hasAttribute('data-wp-swatch-preloaded')) return;
    card.setAttribute('data-wp-swatch-preloaded', '');
    var links = card.querySelectorAll('[data-wp-swatch-img]');
    for (var i = 0; i < links.length; i++) {
      var img = new Image();
      img.decoding = 'async';
      img.src = links[i].getAttribute('data-wp-swatch-img');
    }
  };

  h.cardSwatchOn = function (link) {
    var card = link.closest('[data-wp-card]');
    if (!card) return;
    var active = card.querySelectorAll('.wp-card__swatch-link.is-active');
    for (var i = 0; i < active.length; i++) active[i].classList.remove('is-active');
    link.classList.add('is-active');
    var layer = card.querySelector('[data-wp-swatch-layer]');
    var src = link.getAttribute('data-wp-swatch-img');
    if (layer && src) {
      if (layer.getAttribute('src') !== src) layer.setAttribute('src', src);
      card.classList.add('is-swatch');
    } else {
      /* No variant image for this colour: keep the card's own image */
      card.classList.remove('is-swatch');
    }
  };

  h.cardReset = function (card) {
    if (!card) return;
    card.classList.remove('is-swatch');
    var active = card.querySelectorAll('.wp-card__swatch-link.is-active');
    for (var i = 0; i < active.length; i++) active[i].classList.remove('is-active');
  };

  h.onCardOver = function (event) {
    if (WP.isTouch()) return;
    var card = event.target.closest && event.target.closest('[data-wp-card]');
    if (!card) return;
    h.loadCardHover(card);
    h.cardPreload(card);
    var link = event.target.closest('.wp-card__swatch-link');
    if (link) h.cardSwatchOn(link);
  };

  h.onCardOut = function (event) {
    var card = event.target.closest && event.target.closest('[data-wp-card]');
    if (!card) return;
    if (event.relatedTarget && card.contains(event.relatedTarget)) return;
    h.cardReset(card);
  };

  h.onCardFocus = function (event) {
    var link = event.target.closest && event.target.closest('.wp-card__swatch-link');
    if (!link) return;
    /* Keyboard focus only: a tap or click also focuses the link, and on touch the first tap
       must be handled by onDocumentClick (preview first, open on the second tap). */
    if (WP.isTouch()) return;
    try {
      if (!link.matches(':focus-visible')) return;
    } catch (e) {
      /* older browsers: treat as keyboard focus */
    }
    h.cardPreload(link.closest('[data-wp-card]'));
    h.cardSwatchOn(link);
  };

  h.onCardBlur = function (event) {
    var card = event.target.closest && event.target.closest('[data-wp-card]');
    if (!card) return;
    if (event.relatedTarget && card.contains(event.relatedTarget)) return;
    if (!card.matches(':hover')) h.cardReset(card);
  };

  /* ---------- init ---------- */

  WP.init = function () {
    WP._h.initHeader();
    WP._h.initAccordions();
    WP._h.initTabs();
    WP._h.initDeferredView();
  };

  function bindGlobal() {
    if (state.bound) return;
    state.bound = true;

    window.addEventListener(
      'scroll',
      function () {
        WP._h.onScroll();
      },
      { passive: true }
    );
    document.addEventListener(
      'scroll',
      function (event) {
        if (event.target && event.target.classList && event.target.classList.contains('page-wrapper')) WP._h.onScroll();
      },
      { passive: true, capture: true }
    );
    window.addEventListener('resize', function () {
      WP._h.writeHeaderHeight();
    });
    window.addEventListener('hashchange', function () {
      WP._h.onHashChange();
    });
    document.addEventListener('click', function (event) {
      WP._h.onDocumentClick(event);
    });
    document.addEventListener('mouseover', function (event) {
      WP._h.onCardOver(event);
    });
    document.addEventListener('mouseout', function (event) {
      WP._h.onCardOut(event);
    });
    document.addEventListener('focusin', function (event) {
      WP._h.onCardFocus(event);
    });
    document.addEventListener('focusout', function (event) {
      WP._h.onCardBlur(event);
    });

    var cartEvents = ['cart:update', 'cart:refresh', 'monster:cart:updated', 'cart-section:restored'];
    for (var i = 0; i < cartEvents.length; i++) {
      document.addEventListener(cartEvents[i], function () {
        WP.cartCount();
      });
    }
    window.addEventListener('beforeprint', function () {
      WP._h.revealAllDeferred();
    });
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        var cards = document.querySelectorAll('[data-wp-card].is-swatch');
        for (var c = 0; c < cards.length; c++) WP._h.cardReset(cards[c]);
      }
      if (event.persisted) WP.cartCount();
    });

    document.addEventListener('shopify:section:load', function () {
      WP.init();
      WP._h.loadAllCardHovers();
    });

    if (document.readyState === 'complete') {
      WP._h.loadAllCardHovers();
    } else {
      window.addEventListener('load', function () {
        WP._h.loadAllCardHovers();
      });
    }
  }

  /* Speed batch C: the header prints cart.item_count server-side, so a normal page view needs no
     /cart.js request. Only a back/forward navigation can show an older copy of the page (from the
     HTTP cache); then the count is fetched (bfcache restores are handled in 'pageshow'). Cart
     changes keep refreshing the count through watchCartRequests and the cart events. */
  function needsCartCount() {
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (nav) return nav.type === 'back_forward';
      return !!(performance.navigation && performance.navigation.type === 2);
    } catch (e) {
      return true;
    }
  }

  function start() {
    watchCartRequests();
    bindGlobal();
    WP.init();
    if (needsCartCount()) WP.cartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
